import React, { useEffect, useState } from 'react';
import { Modal, View, StyleSheet, Pressable, Text } from 'react-native';
import I18n from '../../../../components/shared/i18n/I18n.component';
import CustomDatePicker from './Datepicker.component';
import ImageIcon from '../../../../components/shared/Icon/ImageIcon.component';
import colors from '../../../../config/colors';
import MonthPicker from './MonthPicker.component';
import YearPicker from './YearPicker.component';

interface CompareModalProps {
	modalVisible: boolean;
	setModalVisible: (visible: boolean) => void;
	onCompare: (params: {
		period: 'monthly' | 'daily';
		criterias: { date_from: string; date_to: string; key: string }[];
		selectedTab: number;
	}) => void;
	onReset: () => void;
}

const CompareModal = ({ modalVisible, setModalVisible, onCompare, onReset }: CompareModalProps) => {
	const [selectedTab, setSelectedTab] = useState(0);
	const tabs = ['ימים', 'שבועות', 'חודשים', 'שנים'];
	
	const [fromPeriod, setFromPeriod] = useState<any>(null);
	const [toPeriod, setToPeriod] = useState<any>(null);

	const [resetKey, setResetKey] = useState(0);

	useEffect(() => {
		console.log('Selected Tab:', selectedTab);
	}, [selectedTab]);

	const handleFromDateChange = (date: any) => {
		setFromPeriod(date);
		console.log('From period changed:', date);
	};

	const handleToDateChange = (date: any) => {
		setToPeriod(date);
		console.log('To period changed:', date);
	};

	const buildDateCriteria = (period: any, key: string) => {
		if (!period) return null;

		switch (selectedTab) {
			case 0: // Days - single day
				if (!period.dateString) return null;
				return {
					date_from: period.dateString,
					date_to: period.dateString,
					key,
				};
			case 1: // Weeks - date range
				if (!period.startDate || !period.endDate) return null;
				return {
					date_from: period.startDate,
					date_to: period.endDate,
					key,
				};
			case 2: // Months - full month
				if (!period.value && period.value !== 0) return null;
				const year = period.year || new Date().getFullYear();
				const month = period.value;
				console.log('compare modal month: ', month);
				const startOfMonth = `${year}-${String(month).padStart(2, '0')}-01`;
				const lastDay = new Date(year, month, 0).getDate();
				const endOfMonth = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
				return {
					date_from: startOfMonth,
					date_to: endOfMonth,
					key,
				};
			case 3: // Years - full year
				if (!period || isNaN(Number(period))) return null;
				return {
					date_from: `${period}-01-01`,
					date_to: `${period}-12-31`,
					key,
				};
			default:
				return null;
		}
	};

	// Function to fetch data based on selected dates
	const handleCompare = async () => {
		console.log('Comparing:', { fromPeriod, toPeriod, selectedTab });
		
		if (!fromPeriod || !toPeriod) {
			console.log('Please select both periods');
			return;
		}

		const firstCriteria = buildDateCriteria(fromPeriod, 'first');
		const secondCriteria = buildDateCriteria(toPeriod, 'second');

		if (!firstCriteria || !secondCriteria) {
			console.log('Invalid date criteria');
			return;
		}

		const period = selectedTab === 0 || selectedTab === 1 ? 'daily' : 'monthly';

		onCompare({
			period,
			criterias: [firstCriteria, secondCriteria],
			selectedTab,
		});
		
		setModalVisible(false);
	};

	const handleReset = () => {
		setFromPeriod(null);
		setToPeriod(null);
		setResetKey(prev => prev + 1);
		onReset();
	};

	return (
		<Modal
			animationType='fade'
			transparent={true}
			visible={modalVisible}
			onRequestClose={() => setModalVisible(false)}
		>
			<View style={styles.modalOverlay}>
				<View style={styles.modalContent}>
					<View style={{ gap: 24 }}>
						<View style={{ alignItems: 'center' }}>
							<I18n size={17} weight='600'>
								repReports.workingTimeReportDetail.comparison
							</I18n>
						</View>
						{/* days/weeks/month/years button */}

						<View style={styles.segmentedControl}>
							{tabs.map((tab, index) => (
								<Pressable
									key={index}
									style={({ hovered }) => [
										styles.segment,
										hovered && styles.segmentHover,
										selectedTab === index &&
											styles.segmentSelected,
									]}
									onPress={() => setSelectedTab(index)}
								>
									<Text
										style={[
											styles.segmentText,
											selectedTab === index &&
												styles.segmentTextSelected,
										]}
									>
										{tab}
									</Text>
								</Pressable>
							))}
						</View>

						{/* open custom picker depend on selecting tab */}

						<View
							style={{
								display: 'flex',
								flexDirection: 'row-reverse',
								gap: 24,
							}}
						>
							<View style={{ gap: 8, flex: 1 }}>
								<I18n size={15} weight='400'>
									fromPeriod
								</I18n>
								{selectedTab === 0 && (
									<CustomDatePicker 
										key={resetKey}
										isRange={false} 
										onDateChange={handleFromDateChange}
									/>
								)}
								{selectedTab === 1 && (
									<CustomDatePicker 
										key={resetKey}
										isRange={true} 
										onDateChange={handleFromDateChange}
									/>
								)}
								{selectedTab === 2 && (
									<MonthPicker 
										key={resetKey}
										onDateChange={handleFromDateChange}
									/>
								)}
                                {selectedTab === 3 && (
									<YearPicker 
										key={resetKey}	
										onDateChange={handleFromDateChange}
									/>
								)}
							</View>

							<View style={{ gap: 8, flex: 1 }}>
								<I18n size={15} weight='400'>
									toPeriod
								</I18n>
								{selectedTab === 0 && (
									<CustomDatePicker 
										key={resetKey}
										isRange={false} 
										onDateChange={handleToDateChange}
									/>
								)}
								{selectedTab === 1 && (
									<CustomDatePicker
										key={resetKey}
										isRange={true} 
										onDateChange={handleToDateChange}
									/>
								)}
								{selectedTab === 2 && (
									<MonthPicker 
										key={resetKey}
										onDateChange={handleToDateChange}
									/>
								)}
                                {selectedTab === 3 && (
									<YearPicker 
										key={resetKey}	
										onDateChange={handleToDateChange}
									/>
								)}
							</View>
						</View>
					</View>

					<View style={styles.BottomButtonWrapper}>
						<Pressable
							onPress={handleReset}
							style={({ hovered }) => [
								styles.resetButton,
								hovered && styles.resetButtonHover,
							]}
						>
							<ImageIcon name={'refresh'} />
							<I18n
								size={15}
								weight='400'
								color={colors.titlePrimary}
							>
								general.reset
							</I18n>
						</Pressable>

						<View style={styles.spaceBetweenButtons}>
							<Pressable
								onPress={() => {
									setModalVisible(false);
								}}
								style={({ hovered }) => [
									styles.resetButton,
									hovered && styles.resetButtonHover,
								]}
							>
								<I18n
									size={15}
									weight='400'
									color={colors.titlePrimary}
								>
									general.cancel
								</I18n>
							</Pressable>

							<Pressable
								onPress={handleCompare}
								style={({ hovered }) => [
									styles.compareButton,
									hovered && styles.compareButtonHover,
								]}
							>
								<I18n weight='400' size={14} color='white'>
									general.compare
								</I18n>
							</Pressable>
						</View>
					</View>
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modalOverlay: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
	},
	modalContent: {
		backgroundColor: 'white',
		borderRadius: 10,
		padding: 24,
		width: '90%',
		maxWidth: 700,
		height: '90%',
		maxHeight: 700,
		gap: 32,
		boxShadow: '0 2px 4px 0 rgba(163, 163, 163, 0.25)',
		justifyContent: 'space-between',
	},
	spaceBetweenButtons: {
		display: 'flex',
		flexDirection: 'row-reverse',
		justifyContent: 'center',
		alignItems: 'center',
		gap: 10,
	},
	compareButton: {
		display: 'flex',
		flex: 1,
		flexDirection: 'row-reverse',
		backgroundColor: colors.titlePrimary,
		paddingHorizontal: 25,
		paddingVertical: 9,
		marginLeft: 15,
		borderRadius: 10,
		height: 31,
		gap: 10,
		justifyContent: 'center',
		alignItems: 'center',
	},
	compareButtonHover: {
		backgroundColor: colors.primaryDark,
	},
	BottomButtonWrapper: {
		display: 'flex',
		flexDirection: 'row-reverse',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 24,
	},
	resetButton: {
		display: 'flex',
		flexDirection: 'row-reverse',
		padding: 11,
		borderRadius: 10,
		justifyContent: 'center',
		alignItems: 'center',
		gap: 10,
	},
	resetButtonHover: {
		backgroundColor: colors.secondaryLight,
	},

	segmentedControl: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'center',
		borderWidth: 1,
		borderColor: colors.datepickerSeparator,
		borderRadius: 100,
		padding: 4,
		gap: 4,
		width: '100%',
		maxWidth: 420,
		alignSelf: 'center',
	},
	segment: {
		flex: 1,
		paddingVertical: 10,
		paddingHorizontal: 32,
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 100,
	},
	segmentHover: {
		backgroundColor: colors.iconBG,
	},
	segmentSelected: {
		backgroundColor: colors.titlePrimary,
	},
	segmentText: {
		fontSize: 14,
		color: colors.darkGrey,
		fontWeight: '400',
		fontFamily: 'Rubik',
	},
	segmentTextSelected: {
		color: '#FFFFFF',
		fontWeight: '500',
	},
});

export default CompareModal;
